import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { updateInvoiceExporterCode } from '@/modules/Invoice/services'
import EditIcons from '@/icons/EditIcons'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface InvoiceExporterCodeProps {
    invoiceID: number
    code: string
}

const InvoiceExporterCode: React.FC<InvoiceExporterCodeProps> = ({
    invoiceID,
    code,
}) => {
    const { t } = useTranslation()
    const [isEditing, setIsEditing] = useState(false)
    const [value, setValue] = useState(code)
    const inputRef = useRef<HTMLInputElement>(null)

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateInvoiceExporterCode,
        },
        {
            reactQueryKeys: [QUERY_KEYS.INVOICES],
            successMessage: t('update-invoice-code-success'),
            errorMessage: t('update-invoice-code-error'),
        }
    )

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus()
            inputRef.current?.select()
        }
    }, [isEditing])

    const handleBlur = () => {
        setIsEditing(false)
        if (value !== code) {
            mutateAsync({ invoiceID, code: value })
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            inputRef.current?.blur()
        }
        if (e.key === 'Escape') {
            setValue(code)
            setIsEditing(false)
        }
    }

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={value}
                placeholder={t('invoice:enter-exporter-code')}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isPending}
                className={cn(
                    'w-full h-6 px-1.5 text-xs border rounded-md bg-white',
                    'focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary',
                    isPending && 'opacity-50 cursor-not-allowed'
                )}
            />
        )
    }

    return (
        <div className="flex items-center gap-1 group">
            <span className="text-xs">{code || '---'}</span>
            <button
                onClick={() => setIsEditing(true)}
                className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-opacity"
            >
                <EditIcons className="w-3 h-3 text-gray-500" />
            </button>
        </div>
    )
}

export default InvoiceExporterCode
